import React, { Fragment } from 'react'
import type { Dispatch, SetStateAction } from 'react'

import type { Deal } from '../types/deal'
import type { Invoice, InvoiceStatus } from '../types/invoice'
import { formatCents } from '../utils/formatCents'

import { HandcuffIcon, LightningIcon } from './icons'
import { InvoiceDetailPanel } from './InvoiceDetailPanel'

function statusClass(status: InvoiceStatus): string {
  if (status === 'Paid') return 'paid'
  if (status === 'Unpaid') return 'unpaid'
  if (status === 'Scheduled') return 'scheduled'
  if (status === 'Voided') return 'voided'
  if (status === 'Draft') return 'draft'
  return 'partiallypaid'
}

export interface InvoiceRowEditingState {
  isExpanded: boolean
  isPendingPartial: boolean
  focusPaid: boolean
  isEditingPeriod: boolean
  periodDraft: { start: string; end: string }
  isEditingQuote: boolean
  quoteDraft: string
}

export interface InvoiceRowHandlers {
  toggleRow: (invoiceNumber: string) => void
  applyStatusChange: (invoice: Invoice, value: InvoiceStatus) => void
  voidInvoice: (invoice: Invoice) => void
  restoreInvoice: (invoice: Invoice) => void
  setPeriodDraft: Dispatch<SetStateAction<{ start: string; end: string }>>
  startEditPeriod: (invoice: Invoice) => void
  cancelEditPeriod: () => void
  saveEditPeriod: (invoice: Invoice) => void
  setQuoteDraft: (value: string) => void
  startEditQuote: (invoice: Invoice) => void
  cancelEditQuote: () => void
  saveEditQuote: (invoice: Invoice) => void
  onClickSend: (invoice: Invoice) => void
  onClickPreview: (invoice: Invoice) => void
  onFocusPaidConsumed: () => void
  onPaidBlur: () => void
  onShipmentClick: () => void
}

export interface InvoiceRowUIFlags {
  showShipment: boolean
}

interface Props {
  invoice: Invoice
  deal: Deal
  invoiceIndex: number
  invoices: Invoice[]
  editingState: InvoiceRowEditingState
  handlers: InvoiceRowHandlers
  uiFlags: InvoiceRowUIFlags
}

export const InvoiceRow: React.FunctionComponent<Props> = (props) => {
  const { invoice, deal, invoiceIndex, invoices, editingState, handlers, uiFlags } = props
  const {
    isExpanded,
    isPendingPartial,
    focusPaid,
    isEditingPeriod,
    periodDraft,
    isEditingQuote,
    quoteDraft,
  } = editingState
  const {
    toggleRow,
    applyStatusChange,
    voidInvoice,
    restoreInvoice,
    setPeriodDraft,
    startEditPeriod,
    cancelEditPeriod,
    saveEditPeriod,
    setQuoteDraft,
    startEditQuote,
    cancelEditQuote,
    saveEditQuote,
    onClickSend,
    onClickPreview,
    onFocusPaidConsumed,
    onPaidBlur,
    onShipmentClick,
  } = handlers
  const { showShipment } = uiFlags

  const isManual = invoice.origin === 'manual'
  const sc = statusClass(invoice.status)
  const isVoided = invoice.status === 'Voided'
  const rowCls =
    'inv-row' +
    (isManual ? ' inv-row-addon' : '') +
    (isExpanded ? ' expanded' : '') +
    (isVoided ? ' inv-row-voided' : '')
  const dataAttrs: Record<string, string> = {
    'data-origin': isManual ? 'manual' : 'auto',
  }
  if (isManual && invoice.addonType) dataAttrs['data-addon-type'] = invoice.addonType

  return (
    <Fragment>
      <tr
        className={rowCls}
        {...dataAttrs}
        onClick={() => toggleRow(invoice.invoiceNumber)}
        style={{ cursor: 'pointer' }}
      >
        <td
          className="mono"
          style={{
            color: '#3b82f6',
            fontFamily: 'var(--mono)',
            fontWeight: 500,
            fontSize: 12.5,
            letterSpacing: '-0.01em',
          }}
        >
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 4 }}>
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <span className="inv-chevron">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width={10}
                  height={10}
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={2.6}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <polyline points="9 18 15 12 9 6" />
                </svg>
              </span>
              <span>{invoice.invoiceNumber}</span>
            </div>
            <div
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                flexWrap: 'wrap',
                paddingLeft: 18,
              }}
            >
              {isManual ? (
                <span
                  className="origin-chip origin-manual"
                  title="Manually created"
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 3,
                    fontSize: 10,
                    fontFamily: 'inherit',
                    color: '#92400e',
                    background: '#fffbeb',
                    border: '1px solid #fde68a',
                    padding: '1px 6px',
                    borderRadius: 3,
                    fontWeight: 500,
                  }}
                >
                  <HandcuffIcon />
                  Manual
                </span>
              ) : (
                <span
                  className="origin-chip origin-auto"
                  title="Auto-generated by system"
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 3,
                    fontSize: 10,
                    fontFamily: 'inherit',
                    color: '#6366f1',
                    background: '#eef2ff',
                    border: '1px solid #e0e7ff',
                    padding: '1px 6px',
                    borderRadius: 3,
                    fontWeight: 500,
                  }}
                >
                  <LightningIcon />
                  Auto
                </span>
              )}
              {invoice.isCombinedRenewal ? (
                <span
                  style={{
                    fontSize: 10,
                    color: '#10b981',
                    fontFamily: 'inherit',
                    background: '#ecfdf5',
                    padding: '1px 6px',
                    borderRadius: 3,
                  }}
                >
                  Combined Renewal
                </span>
              ) : null}
              {isManual && isEditingQuote ? (
                <span
                  className="inv-quote-edit"
                  onClick={(event) => event.stopPropagation()}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}
                >
                  <input
                    type="text"
                    autoFocus
                    value={quoteDraft}
                    onChange={(event) => setQuoteDraft(event.target.value)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter') saveEditQuote(invoice)
                      else if (event.key === 'Escape') cancelEditQuote()
                    }}
                    placeholder="Q-2026-0001"
                    style={{
                      fontFamily: 'var(--mono)',
                      fontSize: 10,
                      width: 110,
                      height: 22,
                      padding: '1px 6px',
                      border: '1px solid #fde68a',
                      background: '#fffbeb',
                      borderRadius: 3,
                      color: '#92400e',
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => saveEditQuote(invoice)}
                    style={{
                      fontSize: 10,
                      padding: '2px 6px',
                      border: '1px solid #1d1d1f',
                      background: '#1d1d1f',
                      color: 'white',
                      borderRadius: 3,
                      cursor: 'pointer',
                    }}
                  >
                    Save
                  </button>
                  <button
                    type="button"
                    onClick={cancelEditQuote}
                    style={{
                      fontSize: 10,
                      padding: '2px 6px',
                      border: '1px solid #e2e8f0',
                      background: 'white',
                      color: '#475569',
                      borderRadius: 3,
                      cursor: 'pointer',
                    }}
                  >
                    Cancel
                  </button>
                </span>
              ) : isManual && invoice.quoteNumber ? (
                <span
                  className="inv-quote-chip"
                  title="Click to edit sales quote #"
                  style={{
                    fontFamily: 'var(--mono)',
                    fontSize: 10,
                    color: '#92400e',
                    background: '#fffbeb',
                    border: '1px solid #fde68a',
                    padding: '1px 6px',
                    borderRadius: 3,
                    fontWeight: 500,
                    cursor: 'pointer',
                  }}
                  onClick={(event) => {
                    event.stopPropagation()
                    startEditQuote(invoice)
                  }}
                >
                  {invoice.quoteNumber}
                </span>
              ) : isManual ? (
                <span
                  className="inv-quote-add"
                  title="Add sales quote #"
                  onClick={(event) => {
                    event.stopPropagation()
                    startEditQuote(invoice)
                  }}
                  style={{
                    cursor: 'pointer',
                    fontFamily: 'inherit',
                    fontSize: 10,
                    color: '#64748b',
                    background: 'transparent',
                    border: '1px dashed #cbd5e1',
                    padding: '1px 6px',
                    borderRadius: 3,
                    fontWeight: 500,
                  }}
                >
                  + Sales Quote
                </span>
              ) : null}
            </div>
          </div>
        </td>
        <td
          className="inv-period-cell"
          style={{
            fontSize: 12,
            color: 'var(--secondary)',
            cursor: isEditingPeriod ? 'default' : 'pointer',
          }}
          onClick={(event) => {
            if (isEditingPeriod) {
              event.stopPropagation()
              return
            }
            if (invoice.status === 'Voided' || invoice.status === 'Scheduled') return
            event.stopPropagation()
            startEditPeriod(invoice)
          }}
        >
          {isEditingPeriod ? (
            <span
              onClick={(event) => event.stopPropagation()}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}
            >
              <input
                type="date"
                value={periodDraft.start}
                onChange={(event) =>
                  setPeriodDraft((current) => ({ ...current, start: event.target.value }))
                }
                style={{
                  fontSize: 11,
                  height: 26,
                  padding: '0 6px',
                  border: '1px solid #e2e8f0',
                  borderRadius: 4,
                  fontFamily: 'inherit',
                }}
              />
              <span style={{ color: '#94a3b8' }}>–</span>
              <input
                type="date"
                value={periodDraft.end}
                onChange={(event) =>
                  setPeriodDraft((current) => ({ ...current, end: event.target.value }))
                }
                style={{
                  fontSize: 11,
                  height: 26,
                  padding: '0 6px',
                  border: '1px solid #e2e8f0',
                  borderRadius: 4,
                  fontFamily: 'inherit',
                }}
              />
              <button
                type="button"
                onClick={() => saveEditPeriod(invoice)}
                style={{
                  fontSize: 11,
                  padding: '2px 8px',
                  border: '1px solid #1d1d1f',
                  background: '#1d1d1f',
                  color: 'white',
                  borderRadius: 4,
                  cursor: 'pointer',
                }}
              >
                Save
              </button>
              <button
                type="button"
                onClick={cancelEditPeriod}
                style={{
                  fontSize: 11,
                  padding: '2px 8px',
                  border: '1px solid #e2e8f0',
                  background: 'white',
                  color: '#475569',
                  borderRadius: 4,
                  cursor: 'pointer',
                }}
              >
                Cancel
              </button>
            </span>
          ) : (
            invoice.period.label
          )}
        </td>
        <td style={{ fontWeight: 600 }}>{formatCents(invoice.totalCents)}</td>
        <td onClick={(event) => event.stopPropagation()}>
          {invoice.status === 'Scheduled' ? (
            <select className="status-quick-select scheduled locked" disabled>
              <option value="Scheduled">Scheduled</option>
            </select>
          ) : invoice.status === 'Draft' ? (
            <select className="status-quick-select draft locked" disabled>
              <option value="Draft">Draft</option>
            </select>
          ) : isVoided ? (
            <select className="status-quick-select voided locked" disabled>
              <option value="Voided">Voided</option>
            </select>
          ) : (
            <select
              className={`status-quick-select ${isPendingPartial ? 'partiallypaid' : sc}`}
              value={isPendingPartial ? 'Partially Paid' : invoice.status}
              onChange={(event) => applyStatusChange(invoice, event.target.value as InvoiceStatus)}
            >
              <option value="Unpaid">Unpaid</option>
              <option value="Partially Paid">Partially Paid</option>
              <option value="Paid">Paid</option>
            </select>
          )}
        </td>
        <td onClick={(event) => event.stopPropagation()}>
          <div className="action-btns-labeled">
            <button
              type="button"
              className="action-btn-labeled"
              title="Send Invoice"
              onClick={() => onClickSend(invoice)}
            >
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={1.8}
                strokeLinecap="round"
                strokeLinejoin="round"
                width={15}
                height={15}
              >
                <path d="M14.536 21.686a.5.5 0 0 0 .937-.024l6.5-19a.496.496 0 0 0-.635-.635l-19 6.5a.5.5 0 0 0-.024.937l7.93 3.18a2 2 0 0 1 1.112 1.11z" />
                <path d="m21.854 2.147-10.94 10.939" />
              </svg>
            </button>
            <button
              type="button"
              className="action-btn-labeled"
              title="Download PDF"
              onClick={() => onClickPreview(invoice)}
            >
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={1.8}
                strokeLinecap="round"
                strokeLinejoin="round"
                width={15}
                height={15}
              >
                <path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z" />
                <path d="M14 2v4a2 2 0 0 0 2 2h4" />
                <path d="M10 9H8" />
                <path d="M16 13H8" />
                <path d="M16 17H8" />
              </svg>
            </button>
            <button
              type="button"
              className="action-btn-labeled"
              title={isVoided ? 'Restore invoice' : 'Void invoice'}
              onClick={() => (isVoided ? restoreInvoice(invoice) : voidInvoice(invoice))}
            >
              {isVoided ? (
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={1.8}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  width={15}
                  height={15}
                >
                  <path d="M3 7v13a1 1 0 0 0 1 1h16a1 1 0 0 0 1-1V7" />
                  <path d="M21 3H3v4h18V3z" />
                  <path d="M9 14l3-3 3 3" />
                  <path d="M12 11v7" />
                </svg>
              ) : (
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={1.8}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  width={15}
                  height={15}
                >
                  <rect x={3} y={3} width={18} height={4} rx={1} />
                  <path d="M5 7v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7" />
                  <path d="M10 12h4" />
                </svg>
              )}
            </button>
            {showShipment ? (
              <button
                type="button"
                className="action-btn-labeled action-btn-ship"
                title="Open shipment — jump to fulfillment tab"
                onClick={onShipmentClick}
              >
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={1.8}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  width={15}
                  height={15}
                >
                  <path d="M5 18H3c-.6 0-1-.4-1-1V7c0-.6.4-1 1-1h10c.6 0 1 .4 1 1v11" />
                  <path d="M14 9h4l4 4v4c0 .6-.4 1-1 1h-2" />
                  <circle cx={7} cy={18} r={2} />
                  <circle cx={17} cy={18} r={2} />
                </svg>
              </button>
            ) : null}
          </div>
        </td>
      </tr>
      {isExpanded ? (
        <tr className="inv-detail-row" style={{ display: 'table-row' }}>
          <td colSpan={5} style={{ padding: 0, background: 'transparent' }}>
            <div className="inv-detail-panel">
              <InvoiceDetailPanel
                deal={deal}
                invoice={invoice}
                invoiceIndex={invoiceIndex}
                invoices={invoices}
                focusPaid={focusPaid}
                onFocusPaidConsumed={onFocusPaidConsumed}
                onPaidBlur={onPaidBlur}
              />
            </div>
          </td>
        </tr>
      ) : null}
    </Fragment>
  )
}
